<script setup lang="ts">
import { ref, onBeforeMount, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import ProgressSpinner from 'primevue/progressspinner';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import TaxModal from '@/components/modal/TaxModal.vue';
import ManageFlightModal from '@/components/modal/ManageFlightModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const airManagementStore = useAirManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const loading = ref(false);
const filters = ref();
const filterTaxes = ref();
const taxes = ref([]);

const flights = computed(() => airManagementStore.flights);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const dialog = useDialog();
const tabs = ref([
  { id: 1, title: t('management.airline.logo_management') },
  { id: 2, title: t('management.airline.airline_tax_management') }
]);

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    sts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const initFilterTaxes = () => {
  filterTaxes.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taxCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    taxType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    taxDescriptionEn: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    taxDescriptionFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilterTaxes();

const clearFilter = () => {
  initFilters();
};

const clearFilterTax = () => {
  initFilterTaxes();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const onUpdateStatus = async (sk: string, status: string) => {
  try {
    await airManagementStore.updateFlightStatus(sk, status);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

onBeforeMount(async () => {
  try {
    loading.value = true;
    await airManagementStore.getListFlight();
  } finally {
    loading.value = false;
  }
  getListTax();
});

const handleDelete = async (sk: string) => {
  await airManagementStore
    .deleteFlight(sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('flight.notification.delete_success'), life: 3000 });
      airManagementStore.getListFlight();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('flight.notification.delete_failure'), life: 5000 });
    });
};

const managementFlight = (flight, message: string) => {
  const dialogRef = dialog.open(ManageFlightModal, {
    props: {
      header: message,
      modal: true,
      maximizable: true,
      data: flight
    },
    emits: {
      isSave: () => {
        dialogRef.close();
        airManagementStore.getListFlight();
      }
    }
  });
};

const actionTaxes = (data, header) => {
  dialog.open(TaxModal, {
    data: {
      data: data
    },
    props: {
      header,
      modal: true
    },
    emits: {
      updateData: async () => {
        handleUploadData();
      }
    }
  });
};

const handleUploadData = async () => {
  loading.value = true;
  getListTax();
  loading.value = false;
};

const getListTax = async () => {
  taxes.value = await airManagementStore.getListTax();
};

const removeTax = (data) => {
  airManagementStore.removeTax(data.sk).then(() => {
    toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
    getListTax();
  });
};
</script>

<template>
  <LoadingSpinner v-if="loading" />
  <div class="card airline-management">
    <h3 class="h5style">{{ $t('management.airline.airline_management') }}</h3>
    <Tabs :value="1">
      <TabList>
        <Tab v-for="tab in tabs" :key="tab.id" :value="tab.id">{{ tab.title }}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel v-for="tab in tabs" :key="tab.id" :value="tab.id">
          <div v-if="tab.id === 1">
            <DataTable v-model:filters="filters" :value="flights" paginator showGridlines :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['code', 'name', 'sts']">
              <template #header>
                <div class="flex justify-content-between flex-col sm:flex-row">
                  <div class="flex justify-content-left flex-col sm:flex-row gap-3">
                    <Button data-testid="submit-btn-flight" type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
                  </div>
                  <div class="justify-content-right">
                    <span class="p-input-icon-left mb-2">
                      <i class="pi pi-search" />
                      <InputText v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                    </span>
                  </div>
                </div>
              </template>
              <template #empty> {{ $t('management.no_data_found') }} </template>
              <template #loading>
                <ProgressSpinner class="w-3rem h-3rem" strokeWidth="8" animationDuration="2s" />
              </template>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" :header="$t('management.airline.airline_code')" class="break-word">
                <template #body="{ data }">
                  <span class="long-text" v-tooltip="data.code">
                    {{ data.code }}
                  </span>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('common.search_by_airline_code')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.airline.airline_name')" class="break-word">
                <template #body="{ data }">
                  <span class="long-text" v-tooltip="data.name">
                    {{ data.name }}
                  </span>
                </template>
                <template #filter="{ filterModel }"> <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('common.search_by_airline_name')" /> </template
              ></TableColumn>
              <TableColumn field="logo" :header="$t('management.airline.airline_logo')">
                <template #body="{ data }">
                  <img :src="data.logo" :alt="data.logo" class="shadow-4 w-4rem" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="sts" :header="$t('management.airline.active')" v-if="authorizations?.configurationPage?.airline_management_edit">
                <template #body="{ data }">
                  <ToggleSwitch v-model="data.sts" trueValue="OPEN" falseValue="CLOSED" @change="onUpdateStatus(data.sk, data.sts)" />
                </template>
                <template #filter="{ filterModel }">
                  <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
                    <template #option="slotProps">
                      <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
                    </template>
                  </Select>
                </template>
              </TableColumn>
              <TableColumn :header="$t('common.action')" v-if="authorizations?.configurationPage?.airline_management_edit">
                <template #body="{ data }">
                  <div class="flex">
                    <Button icon="pi pi-pencil" class="p-button-text" @click="managementFlight(data, t('management.airline.update_flight'))" />
                    <Button icon="pi pi-trash" class="p-button-text" @click="handleDelete(data.sk)" />
                  </div>
                </template>
              </TableColumn>
            </DataTable>
            <div>
              <Button v-if="authorizations?.configurationPage?.airline_management_edit" :label="$t('management.airline.add_airline')" icon="pi pi-plus" class="p-button-text" @click="managementFlight(undefined, $t('management.airline.add_flight'))" />
            </div>
          </div>
          <div v-if="tab.id === 2">
            <div>
              <div class="flex justify-content-end align-items-center">
                <Button data-testid="submit-btn-tax" type="button" icon="pi pi-plus" :label="$t('management.airline.add_tax_code')" class="p-button-primary mb-2" @click="actionTaxes(null, `${$t('management.airline.add_airport_tax_code')}`)" />
              </div>
            </div>
            <DataTable v-model:filters="filterTaxes" :value="taxes" paginator showGridlines :loading="loading" :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['taxCode', 'taxType', 'taxDescriptionEn', 'taxDescriptionFr']">
              <template #header>
                <div class="flex justify-content-between flex-col sm:flex-row">
                  <div class="flex justify-content-left flex-col sm:flex-row gap-3">
                    <Button type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilterTax()" />
                  </div>
                  <div class="justify-content-right">
                    <span class="p-input-icon-left mb-2">
                      <i class="pi pi-search" />
                      <InputText v-model="filterTaxes['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                    </span>
                  </div>
                </div>
              </template>
              <template #empty> {{ $t('management.no_data_found') }} </template>
              <template #loading>
                <ProgressSpinner class="w-3rem h-3rem" strokeWidth="8" animationDuration="2s" />
              </template>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="taxCode" :header="$t('management.airline.tax_code')" class="break-word">
                <template #body="{ data }">
                  <span>
                    {{ data.taxCode }}
                  </span>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.airline.search_tax_code')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="taxType" :header="$t('management.airline.tax_type')" class="break-word">
                <template #body="{ data }">
                  <span class="long-text" v-tooltip="data.taxType">
                    {{ data.taxType }}
                  </span>
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.airline.search_tax_type')" />
                </template>
                >
              </TableColumn>
              <TableColumn field="taxDescriptionEn" :header="$t('management.airline.tax_description_en')">
                <template #body="{ data }"> {{ data.taxDescriptionEn }}</template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.airline.search_tax_description_en')" />
                </template>
              </TableColumn>
              <TableColumn :show-filter-operator="false" :show-add-button="false" field="taxDescriptionFr" :header="$t('management.airline.tax_description_fr')" v-if="authorizations?.configurationPage?.airline_management_edit">
                <template #body="{ data }">
                  {{ data.taxDescriptionFr }}
                </template>
                <template #filter="{ filterModel }">
                  <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.airline.search_tax_description_fr')" />
                </template>
              </TableColumn>
              <TableColumn :header="$t('common.action')" v-if="authorizations?.configurationPage?.airline_management_edit">
                <template #body="{ data }">
                  <div class="flex">
                    <Button data-testid="edit-btn-tax" icon="pi pi-pencil" class="p-button-text" @click="actionTaxes(data, `${$t('management.airline.update_airport_tax_code')}`)" />
                    <Button icon="pi pi-trash" class="p-button-text" @click="removeTax(data)" />
                  </div>
                </template>
              </TableColumn>
            </DataTable>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>
